import { useNavigate } from 'react-router-dom';
import { ItemWrapper, ItemContent, Wrapper, HoverBox } from './styles';
import Button from '@components/Button';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { PublicEventDetailsResponse } from '@hubs101/booking-api-skd-client/lib/types';
import { useState, useEffect } from 'react';
import { useMobileWidth } from '@utils/hooks';
import { getDynamicText } from '../EventPosterSectionMenu';
import { EventPosterSectionSubMenuItem } from './EventPosterSectionSubMenuItem';

export interface ProcessedMenuItem {
  id: string;
  title: string;
  subtitle: string;
  link: string;
  order: number;
  index?: number;
  isDesktopDisabled?: boolean;
  isTabletDisabled?: boolean;
  isMobileDisabled?: boolean;
  to?: string;
  subject?: string;
  body?: string;
  styling?: string;
  type?: string;
  element?: string;
}

interface EventPosterSectionMenuItemsProps {
  mainMenuItems: ProcessedMenuItem[] | undefined;
  secondaryMenuItems: ProcessedMenuItem[] | undefined;
  themeColor?: string;
  sluggedEvent?: PublicEventDetailsResponse;
  from?: 'scrollable' | 'event-poster';
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
}

export const EventPosterSectionMenuItems = ({
  mainMenuItems,
  secondaryMenuItems,
  themeColor,
  sluggedEvent,
  from = 'event-poster',
  coLocatedEvents,
}: EventPosterSectionMenuItemsProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isMobile = useMobileWidth();

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Close submenu on scroll
  useEffect(() => {
    const handleScroll = () => {
      if (hoveredIndex !== null) {
        setHoveredIndex(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hoveredIndex]);

  return (
    <Wrapper>
      {mainMenuItems?.map((item, index) => {
        const subItems = secondaryMenuItems?.filter(
          subItem => subItem.index === index
        );
        const isHovered = hoveredIndex === index;

        return (
          <ItemWrapper
            key={item.id}
            $themeColor={themeColor}
            $from={from}
            onMouseEnter={() => {
              setHoveredIndex(index);
            }}
            onMouseLeave={() => {
              setHoveredIndex(null);
            }}
            onClick={() => {
              sluggedEvent?.slug &&
                navigate(`/events/${sluggedEvent?.slug}${item.link}`);
            }}
          >
            <ItemContent $themeColor={themeColor} $from={from}>
              {item.title}
            </ItemContent>

            {subItems && subItems.length > 0 && (
              <HoverBox
                $themeColor={themeColor}
                $from={from}
                $isVisible={isHovered}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    width: '100%',
                  }}
                >
                  {subItems.map(subItem => {
                    if (isMobile && subItem?.isMobileDisabled) return null;
                    if (!isMobile && subItem?.isDesktopDisabled) return null;

                    const isButton =
                      subItem.element === 'button' &&
                      ['0', '1'].includes(subItem.styling ?? '');
                    const isPrimary = subItem.styling === '1';

                    return (
                      <EventPosterSectionSubMenuItem
                        key={subItem.id}
                        subItem={subItem}
                        isPrimary={isPrimary}
                        isButton={isButton}
                        sluggedEvent={sluggedEvent}
                        setHoveredIndex={setHoveredIndex}
                        coLocatedEvents={coLocatedEvents}
                      />
                    );
                  })}
                </div>
              </HoverBox>
            )}
          </ItemWrapper>
        );
      })}
    </Wrapper>
  );
};
